import {
  FormAnswer,
  FormCondition,
  FormConditionGroup,
  FormDefinition,
  FormElement
} from '../../types/formSchema';

export type FormAnswerMap = Record<string, FormAnswer>;
export type FormErrorMap = Record<string, string>;

export const answersToMap = (answers: FormAnswer[] = []): FormAnswerMap =>
  answers.reduce<FormAnswerMap>((result, answer) => {
    result[answer.fieldId] = answer;
    return result;
  }, {});

export const answerMapToArray = (answers: FormAnswerMap): FormAnswer[] => Object.keys(answers).map(id => answers[id]);

export const isEmptyAnswerValue = (value: unknown): boolean => {
  if (value === null || typeof value === 'undefined' || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const canCompareAsNumber = (value: unknown): boolean => {
  if (isEmptyAnswerValue(value)) return false;
  if (typeof value === 'boolean') return false;
  return Number.isFinite(Number(value));
};

const areConditionValuesEqual = (actual: unknown, expected: unknown): boolean => {
  if (actual === expected) return true;
  if (canCompareAsNumber(actual) && canCompareAsNumber(expected)) return Number(actual) === Number(expected);
  if (typeof actual === 'boolean' || typeof expected === 'boolean') return String(actual) === String(expected);
  return false;
};

const compare = (condition: FormCondition, answer: FormAnswer | undefined): boolean => {
  const actual = answer?.value;
  const expected = condition.value;

  switch (condition.operator) {
    case 'EMPTY':
      return isEmptyAnswerValue(actual);
    case 'NOT_EMPTY':
      return !isEmptyAnswerValue(actual);
    case 'EQUALS':
      return areConditionValuesEqual(actual, expected);
    case 'NOT_EQUALS':
      return !areConditionValuesEqual(actual, expected);
    case 'CONTAINS':
      return Array.isArray(actual)
        ? actual.some(item => areConditionValuesEqual(item, expected))
        : String(actual ?? '')
            .toLowerCase()
            .includes(String(expected ?? '').toLowerCase());
    case 'IN':
      return Array.isArray(expected) && expected.some(item => areConditionValuesEqual(actual, item));
    case 'GREATER_THAN':
      return Number(actual) > Number(expected);
    case 'GREATER_THAN_OR_EQUAL':
      return Number(actual) >= Number(expected);
    case 'LESS_THAN':
      return Number(actual) < Number(expected);
    case 'LESS_THAN_OR_EQUAL':
      return Number(actual) <= Number(expected);
    case 'BEFORE':
      return new Date(String(actual)).getTime() < new Date(String(expected)).getTime();
    case 'AFTER':
      return new Date(String(actual)).getTime() > new Date(String(expected)).getTime();
    default:
      return false;
  }
};

export const evaluateConditionGroup = (
  group: FormConditionGroup | undefined,
  answers: FormAnswerMap
): boolean => {
  if (!group || !Array.isArray(group.conditions) || group.conditions.length === 0) return true;
  const results = group.conditions.map(condition => compare(condition, answers[condition.fieldId]));
  return group.match === 'ANY' ? results.some(Boolean) : results.every(Boolean);
};

export const isElementVisible = (element: FormElement, answers: FormAnswerMap): boolean => {
  if (!element.visibility || element.visibility.conditions.length === 0) return true;
  const matches = evaluateConditionGroup(element.visibility, answers);
  return element.visibilityEffect === 'HIDE' ? !matches : matches;
};

const isRequiredFlagEnabled = (value: unknown): boolean =>
  value === true || value === 1 || (typeof value === 'string' && value.trim().toLowerCase() === 'true');

export const isFormElementRequired = (element: FormElement): boolean =>
  isRequiredFlagEnabled(element.required) ||
  isRequiredFlagEnabled((element as FormElement & { isRequired?: boolean }).isRequired) ||
  isRequiredFlagEnabled(element.validation?.required);

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

export const validateFormDefinition = (
  definition: FormDefinition,
  answers: FormAnswerMap,
  elementIds?: Set<string>
): FormErrorMap => {
  const errors: FormErrorMap = {};

  definition.elements.forEach(element => {
    if (elementIds && !elementIds.has(element.id)) return;
    if (element.kind !== 'QUESTION' || !isElementVisible(element, answers)) return;

    const answer = answers[element.id];
    const value = answer?.value;
    const isEmpty = isEmptyAnswerValue(value);

    const isRequired = isFormElementRequired(element);

    if (isRequired && isEmpty) {
      errors[element.id] = 'Please answer this question';
      return;
    }

    if (isEmpty) return;

    if (element.type === 'ACKNOWLEDGEMENT' && isRequired && value !== true) {
      errors[element.id] = 'This must be acknowledged';
      return;
    }

    if (element.type === 'NUMBER' || element.type === 'RATING') {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        errors[element.id] = 'Please enter a number';
        return;
      }
      if (typeof element.config?.min === 'number' && numericValue < element.config.min) {
        errors[element.id] = `Please enter a value of at least ${element.config.min}`;
        return;
      }
      if (typeof element.config?.max === 'number' && numericValue > element.config.max) {
        errors[element.id] = `Please enter a value no greater than ${element.config.max}`;
        return;
      }
    }

    if (element.type === 'EMAIL' && !isValidEmail(String(value))) {
      errors[element.id] = 'Please enter a valid email address';
      return;
    }

    if (element.type === 'URL' && !isValidUrl(String(value))) {
      errors[element.id] = 'Please enter a valid URL';
      return;
    }

    if (
      (element.type === 'SINGLE_CHOICE' || element.type === 'MULTIPLE_CHOICE') &&
      element.config?.allowOther &&
      ((Array.isArray(value) && value.includes('__other__')) || value === '__other__') &&
      element.config.otherRequired &&
      !String(answer?.otherValue || '').trim()
    ) {
      errors[element.id] = `${element.config.otherLabel || 'Other'} details are required`;
    }

    if (element.type === 'FILE_UPLOAD' && Array.isArray(value)) {
      const maxFiles = element.config?.maxFiles || 1;
      if (value.length > maxFiles) {
        errors[element.id] = `Please upload no more than ${maxFiles} file(s)`;
        return;
      }
      const invalidFile = value.find((file: any) => {
        if (
          !file ||
          typeof file !== 'object' ||
          !String(file.id || '').trim() ||
          !String(file.name || '').trim() ||
          !/^https?:\/\//i.test(String(file.url || ''))
        ) {
          return true;
        }
        if (element.config?.maxFileSizeBytes && Number(file.size) > element.config.maxFileSizeBytes) return true;
        if (
          element.config?.allowedMimeTypes?.length &&
          !element.config.allowedMimeTypes.includes(String(file.mimeType || ''))
        ) {
          return true;
        }
        return false;
      });
      if (invalidFile) errors[element.id] = 'One or more uploaded files are invalid';
    }
  });

  return errors;
};
